/**
 * DiffResult → HTML. Mirrors the ordering + content of
 * `renderSummary` from ts/src/diff/summary.ts, but emits semantic HTML
 * (`<details>` / `<ul>`) instead of pre-indented text, and paints
 * markers via CSS classes rather than ANSI escapes.
 *
 * Design:
 *  - One `<details>` section per non-empty top-level collection.
 *  - Empty sections are omitted (vs the CLI which omits them too).
 *  - Every leaf carries the engine's pre-rendered `humanLabel` verbatim
 *    — we're a renderer, not an author.
 *  - NoteChanges collapse to a one-line summary when there are more
 *    than NOTE_EXPAND_THRESHOLD of them in a single pattern; user can
 *    expand the inner `<details>` to see every one.
 */

import type {
  DiffResult,
  Change,
  ChangeKind,
  ChannelDiff,
  PatternDiff,
  MixerInsertDiff,
  MixerDiff,
  ArrangementDiff,
  TrackDiff,
  NoteChange,
  NoteChangeKind,
  AutomationChange,
  OpaqueChange,
  ClipMoveGroup,
  ClipBulkGroup,
  ClipModifyGroup,
} from "flpdiff";
import { diffResultIsIdentical } from "flpdiff";
import { h, renderLabel } from "./ui.ts";

const NOTE_EXPAND_THRESHOLD = 10;

export function renderDiff(result: DiffResult): HTMLElement {
  if (diffResultIsIdentical(result)) {
    return h("section", { class: "diff diff--identical" }, [
      h("p", { class: "diff__identical-msg" }, [
        h("span", { class: "marker marker--ok", "aria-hidden": "true" }, "✓"),
        " These two projects are semantically identical.",
      ]),
    ]);
  }

  const sections: HTMLElement[] = [];

  if (result.metadataChanges.length > 0) {
    sections.push(
      renderSection(
        "Metadata",
        result.metadataChanges.length,
        renderChangeList(result.metadataChanges),
      ),
    );
  }

  if (result.channelChanges.length > 0) {
    sections.push(
      renderSection(
        "Channels",
        result.channelChanges.length,
        h("ul", { class: "changes" }, result.channelChanges.map(renderChannelDiff)),
      ),
    );
  }

  if (result.patternChanges.length > 0) {
    sections.push(
      renderSection(
        "Patterns",
        result.patternChanges.length,
        h("ul", { class: "changes" }, result.patternChanges.map(renderPatternDiff)),
      ),
    );
  }

  if (!isMixerEmpty(result.mixerChanges)) {
    sections.push(
      renderSection(
        "Mixer",
        countMixer(result.mixerChanges),
        renderMixerDiff(result.mixerChanges),
      ),
    );
  }

  if (result.arrangementChanges.length > 0) {
    // One expandable section per arrangement — they're typically
    // long (many tracks × many clips), and grouping several under
    // one header makes drill-down awkward.
    for (const arr of result.arrangementChanges) {
      sections.push(renderArrangementSection(arr));
    }
  }

  if (result.opaqueChanges.length > 0) {
    sections.push(
      renderSection(
        "Opaque",
        result.opaqueChanges.length,
        h(
          "ul",
          { class: "changes" },
          result.opaqueChanges.map(renderOpaqueChange),
        ),
      ),
    );
  }

  return h("section", { class: "diff" }, [
    h("p", { class: "diff__summary" }, buildTotalSummary(result)),
    ...sections,
  ]);
}

/**
 * The engine's humanLabel counts top-level *entities* changed (e.g.
 * "4 changes" = 2 channels + 1 insert + 1 arrangement). Producers
 * reading the viewer expect the number to reflect the actual volume
 * of edits — clips added, notes moved, scalar tweaks — so we
 * recompute across the whole tree and swap in the leaf total while
 * keeping the engine's "(N channels, M tracks…)" breakdown.
 */
function buildTotalSummary(result: DiffResult): string {
  const total = countLeafChanges(result);
  const breakdown = result.summary.humanLabel
    .replace(/^\d+\s+changes?\s*/i, "")
    .trim();
  const head = `${total} change${total === 1 ? "" : "s"}`;
  return breakdown ? `${head} ${breakdown}` : head;
}

function countLeafChanges(result: DiffResult): number {
  let n = result.metadataChanges.length + result.opaqueChanges.length;

  for (const c of result.channelChanges) {
    if (c.kind !== "modified") n += 1;
    n += c.changes.length + c.automationChanges.length;
  }
  for (const p of result.patternChanges) {
    if (p.kind !== "modified") n += 1;
    n += p.changes.length + p.noteChanges.length + p.controllerChanges.length;
  }
  for (const i of result.mixerChanges.inserts) {
    if (i.kind !== "modified") n += 1;
    n += i.changes.length;
  }
  n += result.mixerChanges.changes.length;
  for (const a of result.arrangementChanges) {
    if (a.kind !== "modified") n += 1;
    n += a.changes.length;
    for (const t of a.trackChanges) {
      if (t.kind !== "modified") n += 1;
      n += t.changes.length;
    }
  }
  return n;
}

// ---- section container ----

function renderSection(
  title: string,
  count: number,
  body: HTMLElement,
): HTMLElement {
  return h(
    "details",
    { class: "section", open: true },
    [
      h("summary", { class: "section__summary" }, [
        h("span", { class: "section__title" }, title),
        h("span", { class: "section__count" }, String(count)),
      ]),
      h("div", { class: "section__body" }, body),
    ],
  );
}

// ---- change leaves ----

function markerChar(kind: ChangeKind | NoteChangeKind | "moved"): string {
  if (kind === "added") return "+";
  if (kind === "removed") return "-";
  return "~"; // modified | moved
}

function markerClass(kind: ChangeKind | NoteChangeKind | "moved"): string {
  if (kind === "added") return "marker--add";
  if (kind === "removed") return "marker--rem";
  return "marker--mod";
}

function rowClass(kind: ChangeKind | NoteChangeKind | "moved"): string {
  if (kind === "added") return "change change--add";
  if (kind === "removed") return "change change--rem";
  return "change change--mod";
}

function renderMarker(kind: ChangeKind | NoteChangeKind): HTMLElement {
  return h(
    "span",
    { class: `marker ${markerClass(kind)}`, "aria-hidden": "true" },
    markerChar(kind),
  );
}

function renderChangeLeaf(kind: ChangeKind | NoteChangeKind, label: string): HTMLElement {
  const visual = visualKind(kind, label);
  const friendly = frontLoadChangedVerb(normalizeLeafLabel(label));
  return h("li", { class: rowClass(visual) }, [
    renderMarker(visual),
    h("span", { class: "change__label" }, renderLabel(friendly)),
  ]);
}

/**
 * Smooth out two engine label variations so every scalar transition
 * reads uniformly as `<Entity> <prop>: <old> → <new>`:
 *
 *  1. `X renamed from A to B`  →  `X renamed: A → B`
 *  2. `X prop A → B` (no colon between prop and old-value)
 *       →  `X prop: A → B`
 *
 * Step 2 is skipped when the label already contains a `:` before the
 * arrow — the engine has already structured it.
 */
const RENAMED_RE = /^(.+?)\s+renamed\s+from\s+(.+?)\s+to\s+(.+)$/;

function normalizeLeafLabel(label: string): string {
  let out = label.replace(RENAMED_RE, "$1 renamed: $2 → $3");
  out = ensureColonBeforeOldValue(out);
  return out;
}

function ensureColonBeforeOldValue(label: string): string {
  const arrowIdx = label.indexOf(" → ");
  if (arrowIdx < 0) return label;
  // Already punctuated — leave alone.
  if (label.slice(0, arrowIdx).includes(":")) return label;
  // Insert ":" before the last space before the arrow (i.e. just
  // before the old-value token).
  let spaceIdx = arrowIdx - 1;
  while (spaceIdx > 0 && label[spaceIdx] !== " ") spaceIdx--;
  if (spaceIdx <= 0) return label;
  return label.slice(0, spaceIdx) + ":" + label.slice(spaceIdx);
}

/**
 * Engine emits some modified-leaf labels that start with a bare
 * lowercase noun + quoted name (e.g. "clip 'X' at beat 0: length
 * 406.375 → 406.385 beats"). Prepend "Changed " so the row reads as
 * a sentence. Only triggers on that specific shape so we don't touch
 * already-capitalised labels or post-collapse fragments like "at
 * beat 62".
 */
const LOWERCASE_NOUN_PREFIX_RE = /^[a-z]+\s+'/;

function frontLoadChangedVerb(label: string): string {
  if (!LOWERCASE_NOUN_PREFIX_RE.test(label)) return label;
  return `Changed ${label}`;
}

/**
 * Override the engine's `kind` when the label shape makes it clear
 * we're really looking at a property transition rather than a fresh
 * add/remove. The engine tags `unset → value` as "added" because
 * there was no prior value, but in the context of a parent-modified
 * entity (e.g. an Insert that already exists and is being renamed)
 * the user reads these as modifications, not additions.
 *
 * Heuristic: any label carrying the transition arrow `→` or a
 * "renamed from X to Y" / "from X to Y" phrase is visually a mod.
 * Anything else keeps the engine's kind — in particular plain adds
 * like "Added channel sampler 'X'" or "Added clip: 'Y'".
 */
const TRANSITION_PHRASE_RE = /\brenamed\s+from\b.+\bto\b/i;

function visualKind(
  kind: ChangeKind | NoteChangeKind,
  label: string,
): ChangeKind | NoteChangeKind {
  if (kind === "modified") return kind;
  if (label.includes(" → ") || TRANSITION_PHRASE_RE.test(label)) {
    return "modified";
  }
  return kind;
}

function renderChangeList(changes: readonly Change[]): HTMLElement {
  return h(
    "ul",
    { class: "changes" },
    changes.map((c) => renderChangeLeaf(c.kind, c.humanLabel)),
  );
}

// ---- channel ----

function renderChannelDiff(d: ChannelDiff): HTMLElement {
  const nested: HTMLElement[] = [];
  if (d.changes.length > 0) {
    nested.push(
      h(
        "ul",
        { class: "changes changes--nested" },
        d.changes.map((c) => renderChangeLeaf(c.kind, c.humanLabel)),
      ),
    );
  }
  if (d.automationChanges.length > 0) {
    nested.push(renderAutomationList(d.automationChanges));
  }
  return h("li", { class: "change" }, [
    renderMarker(d.kind),
    h("span", { class: "change__label" }, renderLabel(d.humanLabel)),
    ...nested,
  ]);
}

function renderAutomationList(
  auto: readonly AutomationChange[],
): HTMLElement {
  return h(
    "ul",
    { class: "changes changes--nested changes--sub" },
    auto.map((a) => renderChangeLeaf(a.kind, a.humanLabel)),
  );
}

// ---- pattern ----

function renderPatternDiff(d: PatternDiff): HTMLElement {
  const nested: HTMLElement[] = [];
  if (d.changes.length > 0) {
    nested.push(
      h(
        "ul",
        { class: "changes changes--nested" },
        d.changes.map((c) => renderChangeLeaf(c.kind, c.humanLabel)),
      ),
    );
  }
  if (d.noteChanges.length > 0) {
    nested.push(renderNoteChanges(d.noteChanges));
  }
  if (d.controllerChanges.length > 0) {
    nested.push(renderAutomationList(d.controllerChanges));
  }
  return h("li", { class: "change" }, [
    renderMarker(d.kind),
    h("span", { class: "change__label" }, renderLabel(d.humanLabel)),
    ...nested,
  ]);
}

function renderNoteChanges(notes: readonly NoteChange[]): HTMLElement {
  if (notes.length <= NOTE_EXPAND_THRESHOLD) {
    return h(
      "ul",
      { class: "changes changes--nested changes--sub" },
      notes.map((n) => renderChangeLeaf(n.kind, n.humanLabel)),
    );
  }
  // Summarise by kind when there are too many to render inline.
  const counts = countByKind(notes);
  const summary = Object.entries(counts)
    .map(([k, n]) => `${n} ${k}`)
    .join(", ");
  return h(
    "details",
    { class: "note-collapse" },
    [
      h("summary", { class: "note-collapse__summary" }, [
        h("span", { class: "marker marker--mod", "aria-hidden": "true" }, "~"),
        h("span", null, `${notes.length} note changes (${summary})`),
        h("span", { class: "note-collapse__hint" }, "click to expand"),
      ]),
      h(
        "ul",
        { class: "changes changes--nested changes--sub" },
        notes.map((n) => renderChangeLeaf(n.kind, n.humanLabel)),
      ),
    ],
  );
}

function countByKind(notes: readonly NoteChange[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const n of notes) out[n.kind] = (out[n.kind] ?? 0) + 1;
  return out;
}

// ---- mixer ----

function renderMixerDiff(m: MixerDiff): HTMLElement {
  const parts: HTMLElement[] = [];
  if (m.changes.length > 0) {
    parts.push(renderChangeList(m.changes));
  }
  if (m.inserts.length > 0) {
    parts.push(
      h("ul", { class: "changes" }, m.inserts.map(renderMixerInsertDiff)),
    );
  }
  return h("div", { class: "mixer-diff" }, parts);
}

function renderMixerInsertDiff(d: MixerInsertDiff): HTMLElement {
  const nested: HTMLElement[] = [];
  if (d.changes.length > 0) {
    nested.push(
      h(
        "ul",
        { class: "changes changes--nested" },
        d.changes.map((c) => renderChangeLeaf(c.kind, c.humanLabel)),
      ),
    );
  }
  return h("li", { class: "change" }, [
    renderMarker(d.kind),
    h("span", { class: "change__label" }, renderLabel(d.humanLabel)),
    ...nested,
  ]);
}

// ---- arrangement + track ----

/**
 * One top-level `<details>` section per changed arrangement. Content:
 * the arrangement's own humanLabel (in the header) + nested list of
 * scalar changes and per-track diffs inside.
 */
function renderArrangementSection(d: ArrangementDiff): HTMLElement {
  const title = formatArrangementTitle(d);
  const count = d.changes.length + d.trackChanges.length;

  const bodyChildren: Array<HTMLElement | string> = [];

  if (d.changes.length > 0) {
    bodyChildren.push(
      h(
        "ul",
        { class: "changes" },
        d.changes.map((c) => renderChangeLeaf(c.kind, c.humanLabel)),
      ),
    );
  }
  if (d.trackChanges.length > 0) {
    bodyChildren.push(
      h("ul", { class: "changes" }, d.trackChanges.map(renderTrackDiff)),
    );
  }

  return h(
    "details",
    { class: "section section--arrangement", open: true },
    [
      h("summary", { class: "section__summary" }, [
        h("span", { class: "section__title" }, title),
        h("span", { class: "section__count" }, String(count)),
      ]),
      h("div", { class: "section__body" }, bodyChildren),
    ],
  );
}

function formatArrangementTitle(d: ArrangementDiff): string {
  const base = d.name ? `Arrangement '${d.name}'` : "Arrangement";
  if (d.kind === "added") return `${base} (added)`;
  if (d.kind === "removed") return `${base} (removed)`;
  return base;
}

function renderTrackDiff(d: TrackDiff): HTMLElement {
  // Pull per-change lookup so we can move individuals under their
  // group (each group's `changePaths` points back into `d.changes`).
  const changeByPath = new Map<string, Change>();
  for (const c of d.changes) changeByPath.set(c.path, c);

  const consumed = new Set<string>();
  const groupBlocks: HTMLElement[] = [];

  const addGroup = (
    kind: ChangeKind,
    label: string,
    paths: readonly string[],
  ) => {
    const inner: Change[] = [];
    for (const p of paths) {
      const c = changeByPath.get(p);
      if (c) {
        inner.push(c);
        consumed.add(p);
      }
    }
    groupBlocks.push(renderClipGroup(kind, label, inner));
  };

  for (const g of d.clipMoveGroups) addGroup("modified", g.humanLabel, g.changePaths);
  for (const g of d.clipBulkGroups) addGroup(g.kind, g.humanLabel, g.changePaths);
  for (const g of d.clipModifyGroups) addGroup("modified", g.humanLabel, g.changePaths);

  const nested: HTMLElement[] = [];
  if (groupBlocks.length > 0) {
    nested.push(
      h("ul", { class: "changes changes--nested changes--sub" }, groupBlocks),
    );
  }

  // Changes not consumed by any group → render as plain leaves.
  const leftover = d.changes.filter((c) => !consumed.has(c.path));
  if (leftover.length > 0) {
    nested.push(
      h(
        "ul",
        { class: "changes changes--nested changes--sub" },
        leftover.map((c) => renderChangeLeaf(c.kind, c.humanLabel)),
      ),
    );
  }

  return h("li", { class: "change" }, [
    renderMarker(d.kind),
    h("span", { class: "change__label" }, renderLabel(d.humanLabel)),
    ...nested,
  ]);
}

/**
 * A clip group (move / bulk add / bulk remove / modify) rendered as an
 * expandable leaf: the group's human label is the summary; the per-
 * clip changes that belong to the group nest underneath. Collapsed by
 * default — groups typically summarise 5-30 clips each, and expanding
 * every group would drown out the rest of the arrangement.
 *
 * When a group has no backing per-clip changes (synthetic fixtures,
 * tests, future group types), falls back to a plain leaf.
 */
function renderClipGroup(
  kind: ChangeKind,
  label: string,
  inner: readonly Change[],
): HTMLElement {
  if (inner.length === 0) {
    return renderChangeLeaf(kind, label);
  }
  const shortLabels = collapseRedundantInnerLabels(
    inner.map((c) => c.humanLabel),
  );
  return h("li", { class: `${rowClass(kind)} change--group` }, [
    h("details", { class: "group" }, [
      h("summary", { class: "group__summary" }, [
        h(
          "span",
          { class: `marker ${markerClass(kind)}`, "aria-hidden": "true" },
          markerChar(kind),
        ),
        h("span", { class: "change__label" }, renderLabel(frontLoadVerb(label))),
        h("span", { class: "group__count" }, String(inner.length)),
      ]),
      h(
        "ul",
        { class: "changes changes--nested changes--sub group__body" },
        inner.map((c, i) => renderChangeLeaf(c.kind, shortLabels[i]!)),
      ),
    ]),
  ]);
}

/**
 * Engine emits clip-group labels as "N clips of 'X' VERB (rest)". We
 * prefer the verb at the front — it reads more naturally in the
 * viewer where the verb is what the eye lands on first.
 *
 *   "9 clips of 'X' added (length 5.854 beats, beats 62 … 206)"
 *     → "Added 9 clips of 'X' (length 5.854 beats, beats 62 … 206)"
 *
 * Falls through to the original label on any pattern we don't
 * recognise.
 */
const GROUP_VERB_RE =
  /^(\d+\s+clips?\s+of\s+'[^']*')\s+(added|removed|moved|modified)\b(.*)$/;

function frontLoadVerb(label: string): string {
  const m = GROUP_VERB_RE.exec(label);
  if (!m) return label;
  const prefix = m[1]!;
  const verb = m[2]!;
  const rest = m[3]!;
  const capped = verb.charAt(0).toUpperCase() + verb.slice(1);
  return `${capped} ${prefix}${rest}`;
}

/**
 * Strip the shared boilerplate from per-clip labels inside a clip
 * group. The group summary already states the clip name + length +
 * beat range; repeating that on every inner row is noise.
 *
 * Approach: find the longest character-level common prefix + suffix
 * across all labels, align them to whitespace + drop trailing
 * punctuation. KEEP the last 2 "words" of the stripped prefix so the
 * inner rows still read naturally ("at beat 62" rather than bare "62").
 *
 * Falls back to the full label if stripping would produce an empty
 * string — e.g. when labels are identical, or a modify-group's inner
 * changes vary in too many places.
 */
function collapseRedundantInnerLabels(labels: readonly string[]): string[] {
  if (labels.length < 2) return [...labels];

  // Char-level common prefix.
  let prefixEnd = labels[0]!.length;
  for (let i = 1; i < labels.length; i++) {
    const a = labels[0]!;
    const b = labels[i]!;
    let j = 0;
    while (j < prefixEnd && j < b.length && a[j] === b[j]) j++;
    prefixEnd = j;
  }
  // Align to a whitespace boundary so we don't slice mid-word.
  while (prefixEnd > 0 && !/\s/.test(labels[0]![prefixEnd - 1]!)) prefixEnd--;
  // Keep the last 2 words of the stripped prefix as readable context.
  const KEEP_CONTEXT_WORDS = 2;
  let wordsKept = 0;
  while (prefixEnd > 0 && wordsKept < KEEP_CONTEXT_WORDS) {
    while (prefixEnd > 0 && /\s/.test(labels[0]![prefixEnd - 1]!)) prefixEnd--;
    if (prefixEnd === 0) break;
    while (prefixEnd > 0 && !/\s/.test(labels[0]![prefixEnd - 1]!)) prefixEnd--;
    wordsKept++;
  }

  // Char-level common suffix, bounded so it can't overlap the prefix.
  let minTailLen = Infinity;
  for (const l of labels) minTailLen = Math.min(minTailLen, l.length - prefixEnd);
  let suffixLen = 0;
  outer: while (suffixLen < minTailLen) {
    const c = labels[0]![labels[0]!.length - 1 - suffixLen];
    for (let i = 1; i < labels.length; i++) {
      const li = labels[i]!;
      if (li[li.length - 1 - suffixLen] !== c) break outer;
    }
    suffixLen++;
  }
  // Align to a whitespace boundary.
  while (
    suffixLen > 0 &&
    !/\s/.test(labels[0]![labels[0]!.length - suffixLen]!)
  ) {
    suffixLen--;
  }

  return labels.map((l) => {
    let middle = l.slice(prefixEnd, l.length - suffixLen).trim();
    // Drop any dangling punctuation that the suffix alignment couldn't eat.
    middle = middle.replace(/[,;:]+$/, "").trim();
    return middle || l;
  });
}

// ---- opaque ----

function renderOpaqueChange(o: OpaqueChange): HTMLElement {
  return h("li", { class: "change change--mod" }, [
    h("span", { class: "marker marker--mod", "aria-hidden": "true" }, "~"),
    h("span", { class: "change__label" }, renderLabel(o.humanLabel)),
  ]);
}

// ---- helpers ----

function isMixerEmpty(m: MixerDiff): boolean {
  return m.inserts.length === 0 && m.changes.length === 0;
}

function countMixer(m: MixerDiff): number {
  return m.inserts.length + m.changes.length;
}

// Unused, silences TS's unused-import check for group types we reference via `ClipMoveGroup` etc.
export type _Keep =
  | ClipMoveGroup
  | ClipBulkGroup
  | ClipModifyGroup;
